import { useMembership } from '@/features/account/components/Membership/useMembership'
import {
  type SpecialtyPlanCardData,
  type PlanCardData,
} from '@/features/account/components/Membership/DeliveryPlans/types'
import {
  getAnnualCostSavings,
  getAnnualMonthlyCost,
  getPlanInformation,
} from '@/features/account/components/Membership/utils'
import { routes } from '@shared/constants/routes'
import { resetUser } from '@/features/authentication/utils/authentication'
import { type ConfirmationModalProps } from '@/features/shared/components/Modal/ConfirmationModal'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { useGetStudentEnrollmentStatus } from '@/features/account/services/MembershipPlan/hooks'
import { objectStorageUrl } from '@/features/shared/utils/objectStorageUrl'
import { DynamicSnapEBTCardVerificationModal } from '@/features/purchase/components/Modal/EBTVerificationModal/SnapEBTCardVerificationModal/DynamicSnapEBTCardVerificationModal'
import { DynamicStudentPurchaseModal } from '@/features/purchase/components/Modal/PurchaseModals/StudentPurchaseModal/DynamicStudentPurchaseModal'
import { DynamicStudentVerificationSuccessEmailModal } from '@/features/account/components/Modal/StudentVerificationModals/EmailModal/DynamicStudentVerificationSuccessEmailModal'
import { useQuerySubscriptionPlans } from '@/features/account/services/SubscriptionAux/queries'
import {
  getAnnualPlan,
  getMonthlyPlan,
  getSnapEBTPlan,
  getStudentPlan,
} from '@/features/account/services/SubscriptionAux/utils'
import { useSearchParams } from 'next/navigation'
import { useIsPage } from '@/features/shared/hooks/useIsPage'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'

export const useUpgradeInfo = () => {
  const membership = useMembership()
  const upgradePlan = membership.upgradePlans?.[0] ?? null

  const { price: monthlyCost } = membership
  const { price: annualCost, originalPrice, includesRefund } = getPlanInformation(upgradePlan)

  const savings = getAnnualCostSavings(
    monthlyCost,
    1,
    includesRefund ? originalPrice : annualCost,
    12,
  )
  return { upgradePlan, savings }
}

export const useDeliveryPlanCards = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data: plans = [], isLoading } = useQuerySubscriptionPlans({ enabled })

  const { annualPlan, annualCost } = getAnnualPlan(plans)
  const { monthlyPlan } = getMonthlyPlan(plans)
  const annualTrialPeriodDays = annualPlan?.trial_period_days || 0
  const monthlyTrialPeriodDays = monthlyPlan?.trial_period_days || 0
  const annualMonthlyCost = getAnnualMonthlyCost(annualCost)

  const deliveryPlanCards: PlanCardData[] = [
    {
      plan: annualPlan,
      description:
        annualTrialPeriodDays > 0
          ? `Billed annually ($${annualMonthlyCost}/month) after a ${annualTrialPeriodDays}-day free trial.`
          : `Billed annually ($${annualMonthlyCost}/month).`,
      callout: 'Best value',
    },
    {
      plan: monthlyPlan,
      description: `Billed monthly${
        monthlyTrialPeriodDays > 0 ? ` after ${monthlyTrialPeriodDays}-day free trial` : ''
      }.`,
      callout: '',
    },
  ]

  return { deliveryPlanCards, plans, isLoading }
}

export const useDeliveryPlanCardsAndSpecialCards = () => {
  const { plans = [], isLoading, deliveryPlanCards } = useDeliveryPlanCards()

  const { openDialog } = useHandleDialog()
  const { isStudentVerified } = useGetStudentEnrollmentStatus()
  const { monthlyPlan } = getMonthlyPlan(plans)
  const { studentPlan } = getStudentPlan(plans)
  const { snapEBTPlan } = getSnapEBTPlan(plans)
  const { price: monthlyPrice } = getPlanInformation(monthlyPlan)
  const { price: studentPrice, interval: studentInterval } = getPlanInformation(studentPlan)
  const { price: snapEBTPrice, interval: snapEBTInterval } = getPlanInformation(snapEBTPlan)
  const studentTrialPeriodDays = studentPlan?.trial_period_days ?? 0
  const snapEBTTrialPeriodDays = snapEBTPlan?.trial_period_days ?? 0

  const specialPlanCards: SpecialtyPlanCardData[] = []

  // we must ensure the studentPlan exists, because a user
  // with an expired enrollment (after 4 years) will not
  // receive a student plan from delivery plans

  Boolean(studentPlan) &&
    specialPlanCards.push({
      title: 'For students',
      subtitle: `$${studentPrice}/${studentInterval}`,
      description: `Get over 50% off a monthly plan (reg. $${monthlyPrice}) for up to 4 years. Annual verification required.`,
      ...(studentTrialPeriodDays && {
        trialCopy: `${studentTrialPeriodDays}-day free trial, then $${studentPrice}/${studentInterval}.`,
      }),
      buttonCopy: 'Verify now',
      buttonAriaLabel: 'Verify now for student membership',
      buttonOnClick: () =>
        isStudentVerified
          ? openDialog(DynamicStudentPurchaseModal)
          : openDialog(DynamicStudentVerificationSuccessEmailModal),
      imageUrl: objectStorageUrl('student-plan.png'),
      trackingProperties: {
        name: 'student',
        isTrial: Boolean(studentTrialPeriodDays),
      },
    })

  Boolean(snapEBTPlan) &&
    specialPlanCards.push({
      title: 'For SNAP EBT recipients',
      subtitle: `$${snapEBTPrice}/${snapEBTInterval}`,
      description: `Get over 50% off a monthly plan (reg. $${monthlyPrice}). Verification required.`,
      ...(snapEBTTrialPeriodDays && {
        trialCopy: `${snapEBTTrialPeriodDays}-day free trial, then $${snapEBTPrice}/${snapEBTInterval}.`,
      }),
      buttonCopy: 'Verify now',
      buttonAriaLabel: 'Verify now for SNAP EBT membership',
      buttonOnClick: () => {
        openDialog(DynamicSnapEBTCardVerificationModal)
      },
      imageUrl: objectStorageUrl('snap-plan.png'),
      trackingProperties: {
        name: 'ebt',
        isTrial: Boolean(snapEBTTrialPeriodDays),
      },
    })

  return {
    deliveryPlanCards,
    specialPlanCards,
    isLoading,
  }
}

export const useCircle360AccountMismatchModal = (): ConfirmationModalProps => {
  const { GLOBAL_HOMEPAGE } = useIsPage()
  const router = useSegwayRouter()
  const { closeDialog } = useHandleDialog()
  const searchParams = useSearchParams()
  const eventProperties = {
    message_goal: 'circle360_u8_signed_into_a_different_account',
    type: 'button',
  }

  const handleConfirm = () => {
    trackElementClicked({ ...eventProperties, content: 'click_sign_out' })

    const url = new URL(routes.TARGET_CIRCLE_360.url, window.location.origin)
    url.searchParams.set('confirm', searchParams?.get('confirm') ?? '')
    url.searchParams.set('utm_source', 'differentaccountmodal')

    resetUser({
      redirectUrl: `${routes.LOGOUT.url}?returnTo=${encodeURIComponent(url.toString())}`,
    })
  }

  const handleReject = () => {
    trackElementClicked({ ...eventProperties, content: 'click_no_thanks' })
    closeDialog()
    if (!GLOBAL_HOMEPAGE) router.push(routes.GLOBAL_HOMEPAGE.url)
  }

  const handleCloseClick = () => {
    trackElementClicked({ ...eventProperties, content: 'click_close' })
  }

  return {
    contentLabel: 'Sign in with Target',
    modalTitle: 'Looks like you’re signed into a different account',
    modalMessage:
      'To take advantage of your Target Circle 360™ benefits, please sign in to Shipt using the same email as your Target.com account.',
    acceptButtonLabel: 'Sign in with Target',
    handleConfirm,
    rejectButtonLabel: 'No thanks',
    handleReject,
    closeOnReject: false,
    handleCloseClick,
  }
}
